import { type InputHTMLAttributes, forwardRef } from 'react';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, hint, className = '', id, ...props }, ref) => {
    const inputId = id ?? label?.toLowerCase().replace(/\s+/g, '-');

    return (
      <div className={`input-group ${error ? 'input-error' : ''} ${className}`}>
        {label && (
          <label htmlFor={inputId} className="input-label">
            {label}
            {props.required && (
              <span className="input-required" aria-hidden="true"> *</span>
            )}
          </label>
        )}
        <input ref={ref} id={inputId} className="input-field" {...props} />
        {hint && !error && <span className="input-hint">{hint}</span>}
        {error && <span className="input-error-text">{error}</span>}
        <style>{`
          .input-group {
            margin-bottom: var(--space-md);
          }
          .input-label {
            display: block;
            font-weight: 500;
            margin-bottom: var(--space-xs);
            font-size: 0.875rem;
          }
          .input-field {
            width: 100%;
            padding: var(--space-sm) var(--space-md);
            border: 1px solid var(--color-border);
            border-radius: var(--radius-md);
            background: var(--color-surface);
            color: var(--color-text);
            transition: border-color 0.15s;
          }
          .input-field:focus {
            outline: none;
            border-color: var(--color-primary);
          }
          .input-field::placeholder {
            color: var(--color-text-muted);
          }
          .input-required { color: var(--color-urgent); }
          .input-hint {
            display: block;
            font-size: 0.75rem;
            color: var(--color-text-muted);
            margin-top: var(--space-xs);
          }
          .input-error .input-field {
            border-color: var(--color-urgent);
          }
          .input-error-text {
            display: block;
            font-size: 0.75rem;
            color: var(--color-urgent);
            margin-top: var(--space-xs);
          }
        `}</style>
      </div>
    );
  },
);

Input.displayName = 'Input';
